import React, { createContext, useState, useContext, FC } from 'react';
import { useFilterTimeMetric } from 'clientState/hooks/metrics';
import { useOrderFilterTagsActions } from 'clientState/providers/OrderedFilterTagsProvider';

type CarbonFootprintFilterContextType = {
  hasCarbonFootprintFilterApplied: boolean;
  toggleCarbonFootprintFilter: () => void;
  resetCarbonFootprintFilter: () => void;
};

const CarbonFootprintFilterContext =
  createContext<CarbonFootprintFilterContextType>(
    {} as CarbonFootprintFilterContextType
  );

export const useCarbonFootprintFilter =
  (): CarbonFootprintFilterContextType => {
    const context = useContext(CarbonFootprintFilterContext);
    if (!context) {
      throw new Error(
        'useCarbonFootprintFilter must be used within a CarbonFootprintFilterProvider'
      );
    }
    return context;
  };

const CarbonFootprintFilterProvider: FC = ({ children }) => {
  const { start } = useFilterTimeMetric();
  const { add, remove } = useOrderFilterTagsActions();
  const [hasCarbonFootprintFilterApplied, setCarbonFootprintFilter] =
    useState(false);

  const applyCarbonFootprintFilter = (value: boolean) => {
    start();
    setCarbonFootprintFilter(value);
    if (value) {
      add({
        alias: 'carbonFootprint',
        type: 'eco',
        undo: () => {
          setCarbonFootprintFilter(false);
          remove({ alias: 'carbonFootprint' });
        },
      });
    } else {
      remove({ alias: 'carbonFootprint' });
    }
  };

  const value = {
    hasCarbonFootprintFilterApplied,
    toggleCarbonFootprintFilter: () =>
      applyCarbonFootprintFilter(!hasCarbonFootprintFilterApplied),
    resetCarbonFootprintFilter: () => applyCarbonFootprintFilter(false),
  };

  return (
    <CarbonFootprintFilterContext.Provider value={value}>
      {children}
    </CarbonFootprintFilterContext.Provider>
  );
};

export default CarbonFootprintFilterProvider;
